// Minimal WebGL2 helpers: program compilation, attribute layout and growable buffers.

export interface Program {
  prog: WebGLProgram;
  u: Record<string, WebGLUniformLocation | null>;
}

function compile(gl: WebGL2RenderingContext, type: number, src: string, label: string): WebGLShader {
  const sh = gl.createShader(type);
  if (!sh) throw new Error(`globe: createShader failed (${label})`);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS) && !gl.isContextLost()) {
    const log = gl.getShaderInfoLog(sh);
    gl.deleteShader(sh);
    throw new Error(`globe: ${label} ${type === gl.VERTEX_SHADER ? "vertex" : "fragment"} shader: ${log}`);
  }
  return sh;
}

export function createProgram(
  gl: WebGL2RenderingContext,
  vs: string,
  fs: string,
  attribs: string[],
  uniforms: string[],
  label: string,
): Program {
  const prog = gl.createProgram();
  if (!prog) throw new Error(`globe: createProgram failed (${label})`);
  const v = compile(gl, gl.VERTEX_SHADER, vs, label);
  const f = compile(gl, gl.FRAGMENT_SHADER, fs, label);
  gl.attachShader(prog, v);
  gl.attachShader(prog, f);
  attribs.forEach((name, i) => gl.bindAttribLocation(prog, i, name));
  gl.linkProgram(prog);
  gl.deleteShader(v);
  gl.deleteShader(f);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS) && !gl.isContextLost()) {
    throw new Error(`globe: link ${label}: ${gl.getProgramInfoLog(prog)}`);
  }
  const u: Record<string, WebGLUniformLocation | null> = {};
  for (const name of uniforms) u[name] = gl.getUniformLocation(prog, name);
  return { prog, u };
}

/** Attribute layout entry: [location, components] in a tightly packed float stream. */
export type Layout = Array<[number, number]>;

/** Configures float attributes for the currently bound ARRAY_BUFFER. */
export function setupAttribs(gl: WebGL2RenderingContext, layout: Layout, divisor = 0): void {
  const stride = layout.reduce((s, [, n]) => s + n, 0) * 4;
  let off = 0;
  for (const [loc, n] of layout) {
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, n, gl.FLOAT, false, stride, off);
    gl.vertexAttribDivisor(loc, divisor);
    off += n * 4;
  }
}

/**
 * CPU-side float stream that is re-uploaded every frame. Grows geometrically so
 * steady-state frames do not allocate.
 */
export class FloatStream {
  data: Float32Array;
  length = 0;
  constructor(initial = 4096) {
    this.data = new Float32Array(initial);
  }
  reset(): void {
    this.length = 0;
  }
  reserve(n: number): void {
    if (this.length + n <= this.data.length) return;
    let cap = this.data.length * 2;
    while (cap < this.length + n) cap *= 2;
    const next = new Float32Array(cap);
    next.set(this.data.subarray(0, this.length));
    this.data = next;
  }
}

/** Same as FloatStream for 32-bit indices. */
export class IndexStream {
  data: Uint32Array;
  length = 0;
  constructor(initial = 4096) {
    this.data = new Uint32Array(initial);
  }
  reset(): void {
    this.length = 0;
  }
  reserve(n: number): void {
    if (this.length + n <= this.data.length) return;
    let cap = this.data.length * 2;
    while (cap < this.length + n) cap *= 2;
    const next = new Uint32Array(cap);
    next.set(this.data.subarray(0, this.length));
    this.data = next;
  }
}

/** Uploads a stream into a GPU buffer, reallocating the GPU store only when it grows. */
export class DynamicBuffer {
  buf: WebGLBuffer | null;
  private capBytes = 0;
  constructor(private gl: WebGL2RenderingContext, private target: number) {
    this.buf = gl.createBuffer();
  }
  upload(view: Float32Array | Uint32Array, count: number): void {
    const gl = this.gl;
    gl.bindBuffer(this.target, this.buf);
    const bytes = count * 4;
    if (bytes > this.capBytes) {
      this.capBytes = Math.max(bytes, this.capBytes * 2, 1024);
      gl.bufferData(this.target, this.capBytes, gl.DYNAMIC_DRAW);
    }
    if (count > 0) gl.bufferSubData(this.target, 0, view, 0, count);
  }
}
